import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "@/components/dialog";
import { Button } from "@/components/button";
import { Input, InputGroup } from "@/components/input";
import { MagnifyingGlassIcon } from "@heroicons/react/16/solid";
import { Checkbox } from "@/components/checkbox";
import useQueryGetUserList from "@/apis/users/getUserList";
import type { IUser } from "@/apis/users/getMe";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/table";
import { PaginationComponent } from "@/components/PaginationComponent";
import { getPaginationInfo } from "@/types/pagination";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { clsx } from "clsx";

interface GroupMemberModalProps {
  open: boolean;
  onClose: () => void;
  onUpdateMembers: (userIds: string[]) => void;
  currentMembers: string[];
  currentGroupId: string;
}

const GroupMemberModal: React.FC<GroupMemberModalProps> = ({
  open,
  onClose,
  onUpdateMembers,
  currentMembers,
  currentGroupId,
}) => {
  // 선택된 사용자 상태 (초기값으로 현재 멤버들 포함)
  const [selectedUsers, setSelectedUsers] = useState<string[]>(currentMembers);
  // 검색 및 페이지네이션 상태
  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams, setSearchParams] = useState({
    page: 1,
    limit: 5,
    role: "student", // 학생만 필터링 (필요에 따라 변경 가능)
  });

  // 사용자 목록 조회
  const { data, isLoading } = useQueryGetUserList(searchParams);

  // currentMembers가 변경되면 selectedUsers 업데이트
  useEffect(() => {
    setSelectedUsers(currentMembers);
  }, [currentMembers]);

  // 다른 그룹에 속한 사용자가 선택되었는지 확인하는 함수
  const hasUsersFromOtherGroups = () => {
    if (!data?.items) return false;
    return selectedUsers.some((userId) => {
      const user = data.items.find((user) => user.id === userId);
      return user && user.group_id && user.group_id !== currentGroupId;
    });
  };

  // 페이지네이션 정보
  const paginationInfo = data ? getPaginationInfo(data) : null;

  // 검색 핸들러
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams((prev) => ({
      ...prev,
      name: searchTerm,
      page: 1, // 검색 시 첫 페이지로 이동
    }));
  };

  // 페이지 변경 핸들러
  const handlePageChange = (page: number) => {
    setSearchParams((prev) => ({ ...prev, page }));
  };

  // 체크박스 변경 핸들러
  const handleUserSelect = (userId: string, checked: boolean) => {
    if (checked) {
      setSelectedUsers((prev) => [...prev, userId]);
    } else {
      setSelectedUsers((prev) => prev.filter((id) => id !== userId));
    }
  };

  // 모든 사용자 선택/해제 핸들러
  const handleSelectAll = (checked: boolean) => {
    if (checked && data) {
      const allUsers = data.items.map((user) => user.id);
      setSelectedUsers(allUsers);
    } else {
      setSelectedUsers([]);
    }
  };

  // 멤버 업데이트 핸들러
  const handleUpdateMembers = () => {
    onUpdateMembers(selectedUsers);
    onClose();
  };

  // 모달 닫기 핸들러
  const handleClose = () => {
    setSelectedUsers(currentMembers); // 현재 멤버로 리셋
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} size="3xl">
      <DialogTitle>Manage Group Members</DialogTitle>
      <DialogBody>
        <form onSubmit={handleSearch} className="mb-4 flex gap-2">
          <InputGroup className="flex-grow">
            <MagnifyingGlassIcon />
            <Input
              name="search"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
          <Button type="submit">Search</Button>
        </form>

        {isLoading ? (
          <div className="my-4 text-center">Loading users...</div>
        ) : data?.items && data.items.length > 0 ? (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader className="w-12">
                    <Checkbox
                      checked={
                        data.items.length > 0 &&
                        data.items.every((user) =>
                          selectedUsers.includes(user.id)
                        )
                      }
                      onChange={(checked) => handleSelectAll(checked)}
                    />
                  </TableHeader>
                  <TableHeader>Name</TableHeader>
                  <TableHeader>Grade</TableHeader>
                  <TableHeader>School</TableHeader>
                  <TableHeader>Group</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items.map((user: IUser) => {
                  const isCurrentMember = currentMembers.includes(user.id);
                  return (
                    <TableRow
                      key={user.id}
                      className="cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => {
                        handleUserSelect(
                          user.id,
                          !selectedUsers.includes(user.id)
                        );
                      }}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedUsers.includes(user.id)}
                          onChange={(checked) => {
                            handleUserSelect(user.id, checked);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>
                        {user.is_graduated
                          ? "Graduated"
                          : user.grade === 0
                          ? "-"
                          : `Grade ${user.grade}`}
                      </TableCell>
                      <TableCell>{user.school || "-"}</TableCell>
                      <TableCell>
                        {user.group ? user.group.name : "-"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {paginationInfo && (
              <div className="mt-4 flex justify-center">
                <PaginationComponent
                  currentPage={paginationInfo.currentPage}
                  totalPages={paginationInfo.totalPages}
                  hasNextPage={paginationInfo.hasNextPage}
                  hasPrevPage={paginationInfo.hasPrevPage}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        ) : (
          <div className="my-8 text-center">
            <Text className="text-zinc-500">No users found.</Text>
          </div>
        )}

        {/* 다른 그룹 멤버 경고 메시지 */}
        {hasUsersFromOtherGroups() && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Group Transfer Warning
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Some selected users belong to other groups and will be
                    transferred to this group. They will be automatically
                    removed from their current groups.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleUpdateMembers}>
          Update Members ({selectedUsers.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupMemberModal;
